import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import TrackingMap from "../../components/TrackingMap";
import { api, money } from "../../lib/api";
import {
  Package, CheckCircle2, Truck as TruckIcon, Home as HomeIcon,
  Phone, User, Share2, XCircle, Star,
} from "lucide-react";
import { useCompany } from "../../contexts/CompanyContext";
import { whatsappUrl } from "../../lib/whatsapp";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { BottomSheet } from "../../components/ui/bottom-sheet";
import { toast } from "sonner";
import { formatEta } from "../../lib/eta";
import { shortOrderId } from "../../lib/format";

const STEP_LABEL = {
  pending: "Recibido",
  confirmed: "Confirmado",
  on_the_way: "En camino",
  delivered: "Entregado",
  cancelled: "Cancelado",
};

const PAYMENT_STATUS_LABEL = {
  pending: "Pendiente",
  cash_on_delivery: "Pago contra entrega",
  paid: "Pagado",
  failed: "Fallido",
  cancelled: "Cancelado",
  refunded: "Reembolsado",
};
const fmtPaymentStatus = (s) => PAYMENT_STATUS_LABEL[s] || (s ? s.replace(/_/g, " ") : "—");

// Happy-path timeline rendered vertically. Each event becomes a row with a
// timestamp coming from `status_history` (falls back to created_at for the
// first step on legacy orders).
const TIMELINE = [
  { key: "pending",    label: "Recibido",    description: "Tu pedido llegó a la empresa.",       icon: Package },
  { key: "confirmed",  label: "Confirmado",  description: "La empresa confirmó tu pedido.",     icon: CheckCircle2 },
  { key: "on_the_way", label: "En camino",   description: "Tu unidad salió hacia ti.",          icon: TruckIcon },
  { key: "delivered",  label: "Entregado",   description: "Pedido entregado. ¡Gracias!",        icon: HomeIcon },
];

// Friendly transition messages — toast fired when the order moves from one
// status to the next. Keys are the *new* status; values are { msg, kind }.
const TRANSITION_TOAST = {
  confirmed:  { msg: "La empresa confirmó tu pedido.",       kind: "info" },
  on_the_way: { msg: "Tu pedido está en camino.",            kind: "info" },
  delivered:  { msg: "Tu pedido fue entregado. ¡Gracias!",   kind: "success" },
  cancelled:  { msg: "Tu pedido fue cancelado.",             kind: "error" },
};

const fmtTs = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("es", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return null; }
};

export default function OrderDetail() {
  const { id } = useParams();
  const { currentCompany } = useCompany();
  const [order, setOrder] = useState(null);
  const [tracking, setTracking] = useState(null);
  // Rating state — se carga al ver un pedido entregado. Si no hay
  // rating aún, el BottomSheet se abre automáticamente 1 vez para
  // invitar al cliente a calificar.
  const [existingRating, setExistingRating] = useState(null);
  const [ratingOpen, setRatingOpen] = useState(false);
  const [ratingStars, setRatingStars] = useState(0);
  const [driverStars, setDriverStars] = useState(0);
  const [ratingComment, setRatingComment] = useState("");
  const [ratingSaving, setRatingSaving] = useState(false);
  const autoPromptedRef = useRef(false);
  // ETA emitted by the embedded TrackingMap.
  const [eta, setEta] = useState(null);
  // Mirror the latest ETA into a ref so the deferred status-transition
  // notification (which fires inside a setTimeout) can read the freshest
  // value without stale-closure issues.
  const etaRef = useRef(null);
  useEffect(() => { etaRef.current = eta; }, [eta]);
  // Remember the last status we saw so we can fire a toast only on actual
  // transitions (and never on the very first load of the page).
  const prevStatusRef = useRef(null);

  useEffect(() => {
    const loadOrder = () => api.get(`/orders/${id}`).then((r) => setOrder(r.data));
    const loadTracking = () => api.get(`/orders/${id}/tracking`).then((r) => setTracking(r.data)).catch(() => {});
    loadOrder(); loadTracking();
    const t = setInterval(() => { loadOrder(); loadTracking(); }, 8000);
    return () => clearInterval(t);
  }, [id]);

  // Cargar el rating existente (si ya calificó antes). Lo recargamos
  // también cuando `order.status` cambia a `delivered` para que el
  // modal de "calificar" se dispare automáticamente una vez.
  useEffect(() => {
    if (!order || order.status !== "delivered") return;
    let cancelled = false;
    api.get(`/orders/${id}/rating`).then((r) => {
      if (cancelled) return;
      const data = r.data || null;
      setExistingRating(data);
      if (data) {
        setRatingStars(data.stars || 0);
        setDriverStars(data.driver_stars || 0);
        setRatingComment(data.comment || "");
      } else if (!autoPromptedRef.current) {
        // Solo auto-abrir 1 vez por sesión para no ser invasivos.
        autoPromptedRef.current = true;
        setRatingOpen(true);
      }
    }).catch(() => { /* 404 ok: nunca calificado */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, order?.status]);

  const submitRating = async () => {
    if (ratingStars < 1) {
      toast.error("Selecciona al menos 1 estrella para calificar el pedido.");
      return;
    }
    setRatingSaving(true);
    try {
      const { data } = await api.post(`/orders/${id}/rating`, {
        stars: ratingStars,
        comment: ratingComment.trim() || null,
        driver_stars: driverStars > 0 ? driverStars : null,
      });
      setExistingRating(data);
      setRatingOpen(false);
      toast.success("¡Gracias por tu calificación!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar tu calificación.");
    } finally {
      setRatingSaving(false);
    }
  };

  // Ask for browser-notification permission once per session, but only when
  // the order is still "in flight" (no point bothering the user on a
  // delivered/cancelled receipt). Slight delay so it doesn't feel intrusive
  // right after page load.
  useEffect(() => {
    if (!order?.status) return;
    if (["delivered", "cancelled"].includes(order.status)) return;
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission !== "default") return;
    try {
      if (sessionStorage.getItem("notif_asked") === "1") return;
    } catch (_e) { /* ignore */ }
    const t = setTimeout(() => {
      try { sessionStorage.setItem("notif_asked", "1"); } catch (_e) { /* ignore */ }
      try { Notification.requestPermission().catch(() => {}); } catch (_e) { /* ignore */ }
    }, 2500);
    return () => clearTimeout(t);
  }, [order?.status]);

  // Toast + system notification on status transition (skip the initial
  // render). The OS notification only fires when the tab is hidden — when
  // the page is visible the toast is enough and a duplicate notification
  // would feel noisy.
  useEffect(() => {
    if (!order?.status) return;
    const next = order.status;
    const prev = prevStatusRef.current;
    if (prev && prev !== next) {
      const t = TRANSITION_TOAST[next];
      if (t) {
        // Build the user-facing message + push notification body. For the
        // "on_the_way" transition we want to enrich it with the live ETA
        // ("Llega en ~12 min"), but the ETA only lands a couple of seconds
        // after the map's DirectionsService resolves. So we delay the
        // notification briefly when going on_the_way to give it a chance
        // to arrive — every other status fires immediately.
        const fire = (extra) => {
          const fn = toast[t.kind] || toast;
          const msg = extra ? `${t.msg} · ${extra}` : t.msg;
          fn(msg);
          if (
            typeof window !== "undefined" &&
            "Notification" in window &&
            Notification.permission === "granted" &&
            document.visibilityState === "hidden"
          ) {
            try {
              const title = `${currentCompany?.name || "Tu pedido"} · #${shortOrderId(order.order_id)}`;
              const n = new Notification(title, {
                body: msg,
                icon: currentCompany?.logo_url || "/pwa-192.png",
                badge: "/pwa-192.png",
                tag: `order-${order.order_id}`,
                renotify: true,
              });
              n.onclick = () => {
                window.focus();
                n.close();
              };
            } catch (_e) { /* ignore */ }
          }
        };
        if (next === "on_the_way") {
          // 1.8s window for DirectionsService to resolve and emit ETA via
          // onEtaChange → etaRef. Anything still null after that window
          // gets a generic message rather than holding the toast hostage.
          setTimeout(() => {
            const etaInfo = formatEta(etaRef.current, currentCompany?.eta_tolerance_min || 0);
            fire(etaInfo ? `Llega en ~${etaInfo.label}` : null);
          }, 1800);
        } else {
          fire(null);
        }
      }
    }
    prevStatusRef.current = next;
  }, [order?.status, order?.order_id, currentCompany?.name, currentCompany?.logo_url, currentCompany?.eta_tolerance_min]);

  if (!order) return null;

  const status = order.status;
  const isCancelled = status === "cancelled";
  const currentIdx = TIMELINE.findIndex((t) => t.key === status);

  // Status -> map visibility:
  //   pending / cancelled       → no map
  //   confirmed / delivered     → only destination pin
  //   on_the_way                → both pins + route trace
  const showMap = !isCancelled && status !== "pending";

  const destination = order.address?.lat && order.address?.lng
    ? { lat: order.address.lat, lng: order.address.lng }
    : null;
  const driverLoc = tracking?.driver?.current_lat && tracking?.driver?.current_lng
    ? { lat: tracking.driver.current_lat, lng: tracking.driver.current_lng }
    : null;
  const channel = tracking?.driver?.kind || (order.items?.[0]?.channel) || "cilindrera";
  const primary = currentCompany?.primary_color || "#111";
  const contrast = currentCompany?.primary_color_contrast || "#fff";

  // status_history → quick timestamp lookup keyed by status
  const statusAtMap = {};
  for (const ev of order.status_history || []) statusAtMap[ev.status] = ev.at;
  if (!statusAtMap.pending) statusAtMap.pending = order.created_at;

  const shareUrl = `${window.location.origin}/track/${order.order_id}`;
  const shareMsg = `Hola! 🔥 Sigue tu pedido #${shortOrderId(order.order_id)} en tiempo real: ${shareUrl}`;
  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: "Seguimiento de pedido", text: shareMsg, url: shareUrl });
      } else {
        await navigator.clipboard.writeText(shareMsg);
        toast.success("Link copiado al portapapeles");
      }
    } catch (_e) { /* user cancelled */ }
  };
  const clientPhone = order?.client_phone;
  const waHref = clientPhone ? whatsappUrl(clientPhone, shareMsg) : null;

  return (
    <div className="min-h-screen bg-neutral-50">
      <main className="max-w-4xl mx-auto px-4 md:px-6 py-6 space-y-6">
        <div>
          <p className="text-xs text-neutral-400 font-mono">
            Pedido #{shortOrderId(order.order_id)}
          </p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight mt-1">
            {STEP_LABEL[status] || status}
          </h1>
        </div>

        {/* Driver card — only on the way */}
        {tracking?.driver && status === "on_the_way" && (() => {
          const etaInfo = formatEta(eta, currentCompany?.eta_tolerance_min || 0);
          return (
          <div className="bg-white rounded-2xl border border-neutral-200 p-5 space-y-2" data-testid="driver-card">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-11 w-11 rounded-full overflow-hidden border border-neutral-200 bg-company text-[color:var(--company-primary-contrast)] flex items-center justify-center shrink-0">
                  {tracking.driver.image_url ? (
                    <img src={tracking.driver.image_url} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <User size={20} />
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-neutral-400 uppercase tracking-widest">Tu unidad de reparto</p>
                  <p className="font-display font-bold text-lg truncate">{tracking.driver.name}</p>
                  {tracking.driver.vehicle_plate && (
                    <p className="text-xs text-neutral-500">{tracking.driver.vehicle_plate}</p>
                  )}
                </div>
              </div>
              {tracking.driver.phone && (
                <a
                  href={`tel:${tracking.driver.phone}`}
                  className="h-10 w-10 rounded-full bg-neutral-100 flex items-center justify-center hover:bg-neutral-200 shrink-0"
                  data-testid="call-driver-btn"
                >
                  <Phone size={16} />
                </a>
              )}
            </div>
            {etaInfo && (
              <div
                className="mt-3 inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full text-white"
                style={{ background: "var(--company-primary)" }}
                data-testid="driver-eta-pill"
              >
                Llega en ~{etaInfo.label}
              </div>
            )}
          </div>
          );
        })()}

        {/* Map — confirmed / on_the_way / delivered */}
        {showMap && destination && (
          <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden">
            <TrackingMap
              driverLocation={driverLoc}
              destination={destination}
              channel={channel}
              addressType={order.address?.type}
              color={primary}
              contrast={contrast}
              status={status}
              height={320}
              onEtaChange={setEta}
            />
            <div className="flex flex-wrap gap-2 px-4 py-3 border-t border-neutral-100">
              <Button type="button" variant="outline" size="sm" onClick={share} data-testid="share-tracking-btn">
                <Share2 size={14} className="mr-1.5" />Compartir seguimiento
              </Button>
              {waHref && (
                <a
                  href={waHref}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-green-700 hover:text-green-800 hover:underline"
                >
                  o enviar por WhatsApp
                </a>
              )}
            </div>
          </div>
        )}

        {/* Vertical USPS-style timeline */}
        <div className="bg-white rounded-2xl border border-neutral-200 p-5">
          {!isCancelled ? (
            <ol className="relative" data-testid="order-timeline">
              {TIMELINE.map((step, i) => {
                const Icon = step.icon;
                const done = currentIdx > i;
                const active = currentIdx === i;
                const ts = fmtTs(statusAtMap[step.key]);
                const isLast = i === TIMELINE.length - 1;
                return (
                  <li key={step.key} className="relative flex gap-3 pb-5 last:pb-0">
                    {!isLast && (
                      <span
                        className="absolute left-5 top-10 bottom-0 w-[2px]"
                        style={{ background: done ? primary : "#e5e5e5" }}
                        aria-hidden
                      />
                    )}
                    <div className="relative shrink-0 h-10 w-10" style={{ color: primary }}>
                      {active && (
                        <span
                          className="absolute inset-0 h-10 w-10 rounded-full map-pin-pulse"
                          aria-hidden
                        />
                      )}
                      <div
                        className="relative h-10 w-10 rounded-full flex items-center justify-center ring-2 ring-white transition-colors"
                        style={{
                          background: done || active ? primary : "#f5f5f5",
                          color: done || active ? "#fff" : "#737373",
                          boxShadow: active
                            ? "0 4px 10px rgba(0,0,0,0.18), inset 0 -2px 5px rgba(0,0,0,0.15), inset 0 2px 5px rgba(255,255,255,0.25)"
                            : undefined,
                        }}
                        data-testid={`order-step-${step.key}${active ? "-active" : ""}`}
                      >
                        <Icon size={18} />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0 pt-1">
                      <p className={`font-semibold text-sm ${done || active ? "text-neutral-900" : "text-neutral-400"}`}>
                        {step.label}
                      </p>
                      {ts && (
                        <p className="text-[11px] font-mono text-neutral-500 mt-0.5">{ts}</p>
                      )}
                      <p className="text-xs text-neutral-500 mt-0.5 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ol>
          ) : (
            <div className="flex items-center gap-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-3">
              <XCircle size={18} className="shrink-0" />
              <div>
                <p className="font-semibold">Pedido cancelado</p>
                {fmtTs(statusAtMap.cancelled) && (
                  <p className="text-xs text-red-500/80 mt-0.5">{fmtTs(statusAtMap.cancelled)}</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="bg-white rounded-2xl border border-neutral-200 p-5">
          <h3 className="font-display font-bold">Resumen</h3>
          <div className="mt-3 space-y-2 text-sm">
            {order.items.map((i, idx) => (
              <div key={idx} className="flex justify-between">
                <span>{i.name} × {i.quantity}</span>
                <span>{money(i.line_total)}</span>
              </div>
            ))}
            <div className="flex justify-between font-bold font-display text-lg border-t pt-2">
              <span>Total</span><span>{money(order.total)}</span>
            </div>
          </div>
          <div className="mt-4 text-sm space-y-1">
            <p>
              <span className="text-neutral-400">Entrega:</span>{" "}
              {order.when === "now" ? "Inmediata" : new Date(order.scheduled_at).toLocaleString("es")}
            </p>
            <p>
              <span className="text-neutral-400">Pago:</span>{" "}
              {order.payment_method === "cash" ? "Efectivo" : "En línea"} ({fmtPaymentStatus(order.payment_status)})
            </p>
            <p><span className="text-neutral-400">Dirección:</span> {order.address?.full_address}</p>
            {order.notes && <p><span className="text-neutral-400">Notas:</span> {order.notes}</p>}
          </div>
        </div>

        {/* Rating card — sólo para pedidos entregados. Si ya calificó,
            muestra su calificación; si no, un botón para abrir el
            BottomSheet. */}
        {order.status === "delivered" && (
          <div className="mt-5 bg-white rounded-2xl border border-neutral-200 p-5" data-testid="rating-card">
            {existingRating ? (
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-yellow-100 text-yellow-700 flex items-center justify-center shrink-0">
                  <Star size={18} fill="currentColor" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-display font-bold text-base">Tu calificación</p>
                    <StarsDisplay value={existingRating.stars} />
                  </div>
                  {existingRating.comment && (
                    <p className="text-sm text-neutral-600 mt-1">“{existingRating.comment}”</p>
                  )}
                  {existingRating.driver_stars ? (
                    <p className="text-xs text-neutral-500 mt-1">
                      Repartidor: <StarsDisplay value={existingRating.driver_stars} size={12} />
                    </p>
                  ) : null}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setRatingOpen(true)}
                    className="mt-3 h-9"
                    data-testid="rating-edit-btn"
                  >
                    Editar calificación
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-neutral-100 text-neutral-500 flex items-center justify-center shrink-0">
                  <Star size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-display font-bold text-base">¿Cómo te pareció el servicio?</p>
                  <p className="text-xs text-neutral-500 mt-0.5">Tu calificación nos ayuda a mejorar la entrega.</p>
                  <Button
                    onClick={() => setRatingOpen(true)}
                    className="mt-3 h-10 bg-company text-[color:var(--company-primary-contrast)] hover:opacity-90"
                    data-testid="rating-open-btn"
                  >
                    <Star size={14} className="mr-1.5" /> Calificar pedido
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* BottomSheet de calificación — estrellas + comentario. */}
        <BottomSheet
          open={ratingOpen}
          onOpenChange={setRatingOpen}
          title={existingRating ? "Editar calificación" : "Calificar pedido"}
          description={`Pedido #${shortOrderId(order.order_id)}`}
          testid="rating-sheet"
        >
          <div className="px-6 pb-6 space-y-5">
            <div>
              <p className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-2">Servicio general</p>
              <StarsInput value={ratingStars} onChange={setRatingStars} testidPrefix="rating-stars" />
            </div>
            {order.driver_id && (
              <div>
                <p className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-2">Repartidor (opcional)</p>
                <StarsInput value={driverStars} onChange={setDriverStars} testidPrefix="rating-driver-stars" />
              </div>
            )}
            <div>
              <p className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-2">Comentario (opcional)</p>
              <Textarea
                value={ratingComment}
                onChange={(e) => setRatingComment(e.target.value)}
                placeholder="Cuéntanos cómo fue tu experiencia..."
                maxLength={500}
                rows={3}
                data-testid="rating-comment"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1 h-11"
                onClick={() => setRatingOpen(false)}
                data-testid="rating-cancel"
              >
                Cancelar
              </Button>
              <Button
                className="flex-1 h-11 bg-company text-[color:var(--company-primary-contrast)] hover:opacity-90 font-semibold"
                onClick={submitRating}
                disabled={ratingSaving || ratingStars < 1}
                data-testid="rating-submit"
              >
                {ratingSaving ? "Guardando..." : "Enviar calificación"}
              </Button>
            </div>
          </div>
        </BottomSheet>
      </main>
    </div>
  );
}

/** Render-only stars with a given count (1-5). */
function StarsDisplay({ value = 0, size = 14 }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={size}
          fill={i <= value ? "currentColor" : "none"}
          className={i <= value ? "text-yellow-500" : "text-neutral-300"}
        />
      ))}
    </span>
  );
}

/** Clickable stars input. */
function StarsInput({ value = 0, onChange, testidPrefix = "stars" }) {
  return (
    <div className="flex items-center gap-2">
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          type="button"
          key={i}
          onClick={() => onChange(i === value ? 0 : i)}
          className="p-1 tap-fade"
          aria-label={`${i} estrella${i === 1 ? "" : "s"}`}
          data-testid={`${testidPrefix}-${i}`}
        >
          <Star
            size={28}
            fill={i <= value ? "currentColor" : "none"}
            className={i <= value ? "text-yellow-500" : "text-neutral-300 hover:text-neutral-400"}
          />
        </button>
      ))}
    </div>
  );
}
